/**
 * TC credit card service (BE-4) — real API.
 *
 * Cards are liabilities with their own GL account: the balance and history come from the ledger.
 * A bill payment posts immediately (no approval) and can be reversed with a reason.
 * The backend is snake_case; this service returns and takes camelCase.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList } from '../../utils/apiConvert';

const clean = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => v !== '' && v !== undefined),
);
const body = (data) => keysCamelToSnake(clean(data));

const uploadForm = (file) => {
  const form = new FormData();
  form.append('file', file);
  return form;
};

export const creditCardService = {
  getCards: async () => unwrapList(await ApiService.get('/api/tc-credit-cards')).items,

  createCard: async (data) => unwrapData(await ApiService.post('/api/tc-credit-cards', body(data))),

  updateCard: async (id, data) => unwrapData(await ApiService.put(`/api/tc-credit-cards/${id}`, body(data))),

  // Ledger history with running balance; rows of a reversed payment are marked `reversed`.
  getTransactions: async (cardId, { from, to } = {}) => {
    const rows = unwrapList(await ApiService.get(`/api/tc-credit-cards/${cardId}/transactions`, {
      params: clean({ from, to }),
    })).items;
    const reversedEntries = new Set(rows.filter((r) => r.sourceType === 'REVERSAL').map((r) => r.sourceId));
    return rows.map((r) => ({ ...r, reversed: reversedEntries.has(r.journalEntryId) }));
  },

  // data: { amount, sourceAccountId, paidAt, reference }
  payBill: async (cardId, data) =>
    unwrapData(await ApiService.post(`/api/tc-credit-cards/${cardId}/payments`, body(data))),

  reversePayment: async (cardId, paymentId, reason) =>
    unwrapData(await ApiService.post(`/api/tc-credit-cards/${cardId}/payments/${paymentId}/reverse`, { reason })),

  attachReceipt: async (cardId, paymentId, file) =>
    unwrapData(await ApiService.post(`/api/tc-credit-cards/${cardId}/payments/${paymentId}/receipt`, uploadForm(file), {
      headers: { 'Content-Type': 'multipart/form-data' },
    })),

  // Returns { downloadUrl, filename, contentType }; 404 when no receipt is attached.
  receiptUrl: async (cardId, paymentId) =>
    unwrapData(await ApiService.get(`/api/tc-credit-cards/${cardId}/payments/${paymentId}/receipt-url`)),

  // Active OPERATIONS bank and CASH accounts — the only accounts a card bill can be paid from.
  getPayingAccounts: async () => {
    const banks = unwrapList(await ApiService.getTcBankAccounts()).items;
    return banks
      .filter((b) => b.isActive !== false && (b.accountType === 'OPERATIONS' || b.accountType === 'CASH'))
      .map((b) => ({
        id: b.id,
        label: b.accountType === 'CASH'
          ? (b.accountName || 'Cash')
          : [b.bankName, b.accountName].filter(Boolean).join(' - '),
        type: b.accountType,
        balance: b.balance,
      }));
  },
};

export default creditCardService;
